import { __assign, __extends, __rest } from "tslib";
import * as React from 'react';
import { classNamesFunction, FocusZone, FocusZoneDirection, warnMutuallyExclusive } from 'office-ui-fabric-react';
import { NavLink } from './NavLink';
import { NavGroup } from './NavGroup';
import { getNavCountInfo, getNavGroupCountStart } from './Nav.utils';
var getClassNames = classNamesFunction();
var NavBase = /** @class */ (function (_super) {
    __extends(NavBase, _super);
    function NavBase(props) {
        var _this = _super.call(this, props) || this;
        warnMutuallyExclusive('NavBase', _this.props, {
            isNavCollapsed: 'defaultIsNavCollapsed'
        });
        _this.state = {
            isNavCollapsed: !!(props.isNavCollapsed !== undefined ? props.isNavCollapsed : props.defaultIsNavCollapsed),
            shouldScroll: false,
            zoomLevel: window.devicePixelRatio
        };
        _this._menuRef = React.createRef();
        _this._containerRef = React.createRef();
        _this._focusRef = React.createRef();
        _this._onNavCollapseClicked = _this._onNavCollapseClicked.bind(_this);
        _this._setScrollLayout = _this._setScrollLayout.bind(_this);
        _this._updatePixelRatio = _this._updatePixelRatio.bind(_this);
        return _this;
    }
    NavBase.prototype.render = function () {
        var _this = this;
        var _a = this.props, groups = _a.groups, enableCustomization = _a.enableCustomization, showMore = _a.showMore, styles = _a.styles, showMoreLinkProps = _a.showMoreLinkProps, editLinkProps = _a.editLinkProps, collapseNavLinkProps = _a.collapseNavLinkProps, theme = _a.theme, hideCollapseLink = _a.hideCollapseLink;
        var _b = this.state, shouldScroll = _b.shouldScroll, zoomLevel = _b.zoomLevel;
        var isNavCollapsed = this.props.isNavCollapsed === undefined ? this.state.isNavCollapsed : this.props.isNavCollapsed;
        var classNames = getClassNames(styles, { isNavCollapsed: isNavCollapsed, shouldScroll: shouldScroll, zoomLevel: zoomLevel, theme: theme });
        var _c = getNavCountInfo(groups, enableCustomization, showMore, hideCollapseLink), navItemTotal = _c.navItemTotal, editNavIndex = _c.editNavIndex, rest = __rest(_c, ["navItemTotal", "editNavIndex"]);
        var navGroupStartIndex = rest.navGroupStartIndex;
        return (React.createElement("div", { className: classNames.root, role: "presentation" },
            React.createElement("div", { className: classNames.navWrapper, role: "presentation" },
                React.createElement("nav", { role: "navigation", className: classNames.navContainer, ref: this._containerRef },
                    React.createElement("div", { ref: this._menuRef, role: "presentation" },
                        React.createElement(FocusZone, { isCircularNavigation: true, direction: FocusZoneDirection.vertical, as: "ul", role: "menubar", "aria-orientation": "vertical", className: classNames.navGroup, componentRef: this._focusRef },
                            !hideCollapseLink && (React.createElement("li", { role: "presentation" },
                                React.createElement(NavLink, __assign({ primaryIconName: 'GlobalNavButton', role: "menuitem", "aria-expanded": !isNavCollapsed }, collapseNavLinkProps, { onClick: this._onNavCollapseClicked, "aria-posinset": 1, "aria-setsize": navItemTotal })))),
                            groups.map(function (group, groupIndex, array) {
                                // Call getNavGroupCountStart to properly calculate the start index of the next group
                                navGroupStartIndex = getNavGroupCountStart(navGroupStartIndex, groupIndex, array);
                                return (React.createElement(NavGroup, __assign({}, group, { groupIndex: groupIndex, groupName: group.name, isNavCollapsed: isNavCollapsed, navRef: _this._containerRef, focusZoneRef: _this._focusRef, itemStartIndex: navGroupStartIndex, itemTotal: navItemTotal, 
                                    // explicitly setting key here to appease linter as it doesn't get that it's set due to spread
                                    key: group.key })));
                            }),
                            enableCustomization && (
                            // If enableCustomization
                            React.createElement(React.Fragment, null,
                                React.createElement("li", { role: "presentation", className: classNames.navGroupDivider }),
                                React.createElement("li", { role: "presentation" },
                                    React.createElement(NavLink, __assign({ primaryIconName: 'Edit', role: "menuitem" }, editLinkProps, { "aria-setsize": navItemTotal, "aria-posinset": editNavIndex }))),
                                showMore && (React.createElement("li", { role: "presentation" },
                                    React.createElement(NavLink, __assign({ primaryIconName: 'More', role: "menuitem" }, showMoreLinkProps, { "aria-setsize": navItemTotal, "aria-posinset": navItemTotal }))))))))))));
    };
    NavBase.prototype.componentDidMount = function () {
        this._createObserver();
        this._updatePixelRatio();
    };
    NavBase.prototype.componentWillUnmount = function () {
        this._observer.unobserve(this._menuRef.current);
        this._mql.onchange = null;
    };
    NavBase.prototype._createObserver = function () {
        // ref use, call after mount.
        this._observer = new IntersectionObserver(this._setScrollLayout, {
            root: this._containerRef.current,
            // Threshold lowered because Chromium floating point error causes scrollbar to incorrectly appear when zoomed.
            // See Chromium bug for more info: https://bugs.chromium.org/p/chromium/issues/detail?id=737228
            threshold: 0.995
        });
        this._observer.observe(this._menuRef.current);
    };
    NavBase.prototype._updatePixelRatio = function () {
        var _this = this;
        if (this._animationFrameID !== undefined) {
            cancelAnimationFrame(this._animationFrameID);
        }
        this._animationFrameID = requestAnimationFrame(function () {
            if (_this._mql) {
                _this._mql.onchange = null;
            }
            _this._mql = matchMedia("(resolution: " + window.devicePixelRatio + "dppx)");
            _this._mql.onchange = _this._updatePixelRatio;
            _this._animationFrameID = undefined;
        });
        this.setState({ zoomLevel: window.devicePixelRatio });
    };
    NavBase.prototype._setScrollLayout = function (entries) {
        // if we need to scroll set the internal state accordingly only if the state has actually changed
        // Threshold lowered because Chromium floating point error causes scrollbar to incorrectly appear when zoomed.
        // See Chromium bug for more info: https://bugs.chromium.org/p/chromium/issues/detail?id=737228
        var shouldScroll = entries[0].intersectionRatio < 0.995;
        if (shouldScroll !== this.state.shouldScroll) {
            this.setState({ shouldScroll: shouldScroll });
        }
    };
    NavBase.prototype._onNavCollapseClicked = function (ev) {
        // inform the caller about the collapse event
        // collapse this into a single call by extending interface and overriding sig
        if (this.props.onNavCollapsed) {
            this.props.onNavCollapsed(!this.state.isNavCollapsed);
        }
        // additionally call onClick if it was provided in props
        if (this.props.collapseNavLinkProps && this.props.collapseNavLinkProps.onClick) {
            this.props.collapseNavLinkProps.onClick(ev);
        }
        if (this.props.isNavCollapsed === undefined) {
            this.setState({
                isNavCollapsed: !this.state.isNavCollapsed
            });
        }
    };
    return NavBase;
}(React.Component));
export { NavBase };
//# sourceMappingURL=NavBase.js.map